/**
 * Size choice for the "You might also like" tiles in the mini cart.
 *
 * The tiles are rendered by the Frenzy recommendation widget from Frenzy's
 * CDN, which preselects the first in-stock size and leaves Add to bag live.
 * This observer extends the rendered DOM instead:
 * - a tile whose size dropdown offers more than one size opens with no size
 *   selected and Add to bag disabled;
 * - choosing a size lets Frenzy's own handler set that size on Add to bag and
 *   enable it, as it already does;
 * - a one-size tile, or one without a size dropdown, is left as rendered.
 *
 * The widget replaces its tiles after every cart action, so new tiles are
 * reset each time. A tile that is already patched is never reset again, so a
 * size the shopper has chosen stays chosen. Patched selects carry
 * data-size-choice, mirroring the data-a11y-patched guard in
 * frenzy-a11y-patch.js.
 *
 * The first option shows a short visible prompt ("Size"). A select sizes
 * itself to its widest option, so the dropdown's width is measured from its
 * sizes alone and capped there: it is never wider than with sizes only, and
 * Add to bag keeps room for its one-line label. The dropdown is named
 * "Select a size", unless Frenzy already gave it a name.
 */
(function () {
  var SECTION = '.frenzy_cart_popup_section';
  var TILE = '.frenzy_product_item';
  var SELECT = 'select.frenzySelectBox';
  var BUTTON = '.frenzy_auickAdd_cart';
  var AWAITING = 'data-awaiting-size';
  // The most the right padding (the gap before the arrow) may give up so the
  // prompt fits inside the capped width.
  var MAX_PAD_TRIM = 8;
  var canvas;

  function selectSizeLabel() {
    var strings = window.theme && window.theme.variantStrings;
    return (strings && strings.selectSize) || '';
  }

  function sizePrompt() {
    var strings = window.theme && window.theme.variantStrings;
    return (strings && (strings.size || strings.selectSize)) || '';
  }

  // Width of the select from its sizes alone: the prompt is blanked for the
  // measurement and put back, all in one frame, so nothing flickers. The
  // theme animates every property of the select, so transitions are held off
  // while measuring; otherwise the reads land mid-animation.
  function capWidth(select, placeholder) {
    var transition = select.style.transition;
    select.style.transition = 'none';
    var prompt = placeholder.textContent;
    placeholder.textContent = '';
    select.style.maxWidth = '';
    select.style.removeProperty('padding-right');
    var width = select.getBoundingClientRect().width;
    placeholder.textContent = prompt;
    if (width) {
      select.style.maxWidth = width + 'px';
      fitPrompt(select, prompt);
    }
    select.getBoundingClientRect();
    select.style.transition = transition;
    return !!width;
  }

  // If the prompt is wider than the room left in the capped box, take the
  // difference from the right padding, never more than MAX_PAD_TRIM. Frenzy's
  // stylesheet sets that padding as important, so the trim is too.
  function fitPrompt(select, prompt) {
    var style = getComputedStyle(select);
    var padRight = parseFloat(style.paddingRight) || 0;
    var room = select.clientWidth - (parseFloat(style.paddingLeft) || 0) - padRight;
    canvas = canvas || document.createElement('canvas');
    var context = canvas.getContext('2d');
    if (!context) return;
    context.font = [style.fontStyle, style.fontWeight, style.fontSize, style.fontFamily].join(' ');
    var spacing = (parseFloat(style.letterSpacing) || 0) * prompt.length;
    var short = Math.ceil(context.measureText(prompt).width + spacing) + 1 - room;
    if (short > 0) {
      select.style.setProperty('padding-right', Math.max(padRight - MAX_PAD_TRIM, padRight - short) + 'px', 'important');
    }
  }

  // A tile rendered while the mini cart is closed has no width yet, so the
  // cap waits until the select is first laid out.
  function capWhenShown(select, placeholder) {
    if (capWidth(select, placeholder) || typeof ResizeObserver === 'undefined') return;
    var observer = new ResizeObserver(function () {
      if (capWidth(select, placeholder)) observer.disconnect();
    });
    observer.observe(select);
  }

  // The first measurement can run before the store font has loaded, so every
  // capped select is measured again once fonts finish loading.
  function refitAll() {
    document.querySelectorAll(SECTION + ' ' + SELECT + '[data-size-choice]').forEach(function (select) {
      var placeholder = select.options[0];
      if (select.style.maxWidth && placeholder && placeholder.value === '' && placeholder.disabled) {
        capWidth(select, placeholder);
      }
    });
  }

  function sizeOptions(select) {
    return Array.prototype.filter.call(select.options, function (option) {
      return option.value !== '';
    });
  }

  function hasName(select) {
    return select.hasAttribute('aria-label') || select.hasAttribute('aria-labelledby') || (select.labels && select.labels.length > 0);
  }

  function resetTile(tile) {
    var selects = tile.querySelectorAll(SELECT + ':not([data-size-choice])');
    if (!selects.length) return;

    var needsChoice = false;
    selects.forEach(function (select) {
      select.setAttribute('data-size-choice', '1');
      var options = sizeOptions(select);
      if (options.length < 2) return;
      needsChoice = true;

      var label = selectSizeLabel();
      options.forEach(function (option) {
        option.removeAttribute('selected');
        option.selected = false;
      });
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.textContent = sizePrompt();
      placeholder.setAttribute('selected', 'selected');
      select.insertBefore(placeholder, select.firstChild);
      select.selectedIndex = 0;
      capWhenShown(select, placeholder);
      select.setAttribute('aria-required', 'true');
      if (label && !hasName(select)) {
        select.setAttribute('aria-label', label);
      }
    });

    if (!needsChoice) return;
    tile.setAttribute(AWAITING, '');
    tile.querySelectorAll(BUTTON).forEach(function (button) {
      button.disabled = true;
      button.setAttribute('disabled', 'disabled');
    });
  }

  function patch() {
    document.querySelectorAll(SECTION + ' ' + SELECT + ':not([data-size-choice])').forEach(function (select) {
      var tile = select.closest(TILE);
      if (tile) resetTile(tile);
    });
  }

  function onChange(event) {
    var select = event.target;
    if (!select.matches || !select.matches(SECTION + ' ' + SELECT) || select.value === '') return;
    var tile = select.closest(TILE + '[' + AWAITING + ']');
    if (tile) tile.removeAttribute(AWAITING);
  }

  // Nothing reaches the cart from a tile still waiting for a size.
  function onClick(event) {
    var button = event.target.closest && event.target.closest(SECTION + ' ' + BUTTON);
    if (!button || !button.closest(TILE + '[' + AWAITING + ']')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function init() {
    patch();
    new MutationObserver(patch).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', onClick, true);
    if (document.fonts) {
      if (document.fonts.ready) document.fonts.ready.then(refitAll);
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', refitAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
